import * as ft from 'funtypes'
import { ConnectorStandard } from '../domain/connectors.js'
import { PublishedOperatorSlug } from '../domain/operators.js'
import { Site, UnconfirmedReason, VoltageClass } from '../domain/types.js'
import { IsoDateTime, Latitude, Longitude, objectWith } from '../validation.js'

/**
 * The published artifact contracts - the wire format between the pipeline and
 * the frontend.
 *
 * These are frozen. The frontend validates every fetched artifact through the
 * runtypes below, so a bad publish surfaces as a named field path instead of a
 * crash three components deep. Changing a shape means bumping
 * {@link ARTIFACT_SCHEMA_VERSION} and updating both sides in one change.
 */

/**
 * 2 - `meta.json` gained `degraded` and `ocmMatch.byOperator`.
 *
 * The pipeline can publish a set whose voltage is derived-only because Open
 * Charge Map was unreachable. Version 1 had nowhere to say so, which made a
 * degraded publish indistinguishable from a healthy one to anything reading the
 * artifacts - including the next run.
 *
 * 3 - `voltageCoverage` gained `bySource`, `dc` and `ac`; `VoltageClass` stopped
 * being assignable to a DC connector from the plug alone.
 *
 * One `derived` total put a phase count EIPA declared and a guess from the plug
 * in the same bucket, which read as 98% coverage while DC - 93% of it derived
 * from the connector standard - was barely established at all. The class change
 * is the same correction on the other side: `dc-800` was keyed on the top of the
 * standard's window, so every CCS connector got it regardless of power. Readers
 * pinned to 2 will reject a set published by this version, which is the point.
 *
 * 4 - `VoltageSourceSplit.declared` split into `ocmDeclared` and `geDeclared`;
 * `meta.json` gained `geMatch`.
 *
 * GoingElectric declares electrics for about 37% of connectors against Open
 * Charge Map's 1.4%, through two joins of quite different strength - an exact
 * EVSE id and a folded address. Counting all of it as one `declared` number
 * would hide both which source spoke and how firmly, on the field the About page
 * uses to tell the reader how much of this map is measured rather than reasoned.
 * The `declared` headline on `VoltageCoverage` still totals every declaration, so
 * a reader that only wants the one number is unaffected.
 *
 * 7 - sites gained `operatorName`, and `stations.geojson` features gained `opn`.
 *
 * The registry tracks the brands with roughly ten or more sites and folds the
 * rest onto `other`, which is 9 % of the country arriving as one grey bucket.
 * Until now the upstream name was dropped at that point, so nothing downstream
 * could tell a network the registry has not got round to from a hotel car park,
 * and searching for one was answered with nothing. Both fields carry the raw
 * name through, and only for sites in the bucket - a tracked brand's name is
 * already in the registry, and repeating it 919 times would be paid for on
 * first paint.
 *
 * 6 - sites gained `unconfirmed`, and `stations.geojson` features gained `unc`.
 *
 * Some sites cannot be confirmed against any other source - several different
 * stations are listed at one address, or a listing there was reported as gone -
 * and the map now says so on the pin rather than presenting the registry's word
 * as settled. Readers pinned to 5 will reject a set published by this version.
 *
 * 5 - `VoltageInfo` gained `alternatives`; `Connector` gained `disputedPower`.
 *
 * Where two sources that both published a figure disagreed about one connector,
 * the precedence picked a winner and the loser was dropped on the floor - it
 * reached the run log and nothing else. A site payload had nowhere to say "these
 * two databases do not agree about this socket", so the panel could only present
 * the survivor as though nothing had contradicted it. Both fields are optional
 * and absent on an agreeing connector, so the payload of a set with no conflicts
 * is byte-identical to what version 4 published.
 *
 * 8 - `geMatch` gained `byOperator`.
 *
 * The national GoingElectric numbers fall for two quite different reasons - the
 * scrape thinning out everywhere, or one network's sites ceasing to fold to the
 * same address on both sides - and a single `connectorRate` cannot tell them
 * apart. `ocmMatch` has had this split since version 2 for the same reason;
 * carrying it on one of the two matching stages meant the source that declares
 * electrics for a quarter of the map was the one nobody could localise a failure
 * in. Readers pinned to 7 will reject a set published by this version.
 *
 * 9 - `stations.geojson` features carry the reason on `unc`, in place of `true`.
 *
 * The flag said only that something was in doubt, which is one word covering two
 * findings that mean different things to a driver - "several stations are listed
 * here" and "somebody reported this one is gone" - and the second of them only
 * arrived with the detail payload, a click later. `unc` is now the same
 * `UnconfirmedReason` the panel switches on, so a new reason is a compile error
 * on the map as well as in the panel, and a reader pinned to 8 rejects the set
 * rather than reading a slug where it expects a boolean.
 *
 * 10 - chargers the registry does not list are published. `SourceId` gained
 * `other`, `UnconfirmedReason` gained `unlisted`, `Site.updatedAt` and the
 * feature's `upd` became optional, and `geMatch` gained `standaloneSites` and
 * `standaloneConnectors`.
 *
 * The map was built from EIPA alone, and EIPA is not complete: about 1 460
 * Polish sites are absent from it, the 320 kW Porsche HPC on Sekundowa in Warsaw
 * among them. Those now stand on the community dataset that until this version
 * only supplied voltage, under `source: 'other'` and flagged `unlisted` - one
 * source says the charger is there and nothing corroborates it, which is what
 * the unconfirmed flag has always meant. `upd` had to become optional with them:
 * that dataset stamps nothing, and filling the field with the run's own clock
 * would print today's date under "last updated" for a months-old scrape. A
 * reader pinned to 9 rejects the set, which is the point - it would otherwise
 * read a site with no `upd` as a malformed one.
 */
export const ARTIFACT_SCHEMA_VERSION = 10

/** Where each artifact lives, relative to the artifacts base URL. */
export const ARTIFACT_PATHS = {
  stations: 'stations.geojson',
  operators: 'operators.json',
  meta: 'meta.json',
  status: 'status.json',
  prices: 'prices.json',
  /** Per-site detail payload. */
  site: (siteId: string): string => `sites/${siteId}.json`,
} as const

// ---------------------------------------------------------------------------
// stations.geojson
// ---------------------------------------------------------------------------

/**
 * Feature properties use short keys on purpose: this file carries one feature
 * per site for the whole country and is fetched on first paint.
 */
export const StationsFeatureProperties = objectWith(
  {
    /** Site id. Use with `ARTIFACT_PATHS.site(id)` to fetch the detail payload. */
    id: ft.String,
    /** Operator slug; a slug this build has retired reads as `other`. */
    op: PublishedOperatorSlug,
    /** Site name. */
    n: ft.String,
    /** City. */
    c: ft.String,
    /** Number of AC charge points. */
    ac: ft.Number,
    /** Number of DC charge points. */
    dc: ft.Number,
    /** Highest connector power at the site, kW. */
    pmax: ft.Number,
    /** Sorted, unique connector standards present at the site. */
    conn: ft.Array(ConnectorStandard),
    /** Sorted, unique voltage classes present at the site. */
    v: ft.Array(VoltageClass),
  },
  {
    /**
     * Newest upstream timestamp for the site, ISO-8601.
     *
     * Absent on a site whose source publishes no timestamps - see
     * `Site.updatedAt`. Nothing on the map reads it; it is here so a reader can
     * tell a fresh record from a stale one, and a missing key says honestly that
     * this record cannot be told apart either way.
     */
    upd: IsoDateTime,
    /**
     * Why the site is in doubt, present only when something casts doubt on it.
     * The pin carries a warning and the hover card names the finding, neither of
     * them waiting on the detail payload.
     *
     * The reason rather than a bare flag, and the slug rather than a short code:
     * the key is absent on every feature nothing contests, and a handful of
     * repeated slugs across the rest is what gzip is best at, so the honest value
     * costs about what the boolean did. `SiteDetail.unconfirmed` still carries
     * the listing count that goes with it, which the pin has no use for.
     */
    unc: UnconfirmedReason,
    /**
     * The upstream operator name, present only where `op` is `other`.
     *
     * The bucket is one grey dot covering several hundred networks, and this is
     * what lets the search box name them and the filter select one. A tracked
     * brand does not carry it: its name is in the registry already, and repeating
     * it on every feature is a cost paid on first paint.
     */
    opn: ft.String,
  },
)
export type StationsFeatureProperties = ft.Static<typeof StationsFeatureProperties>

/** `[longitude, latitude]`, rounded to 5 decimals. GeoJSON axis order. */
export const StationsFeatureGeometry = ft.Object({
  type: ft.Literal('Point'),
  coordinates: ft.Tuple(Longitude, Latitude),
})
export type StationsFeatureGeometry = ft.Static<typeof StationsFeatureGeometry>

export const StationsFeature = ft.Object({
  type: ft.Literal('Feature'),
  geometry: StationsFeatureGeometry,
  properties: StationsFeatureProperties,
})
export type StationsFeature = ft.Static<typeof StationsFeature>

export const StationsGeoJson = ft.Object({
  type: ft.Literal('FeatureCollection'),
  /** Foreign member: when this artifact was generated. */
  generated: IsoDateTime,
  schemaVersion: ft.Number,
  features: ft.Array(StationsFeature),
})
export type StationsGeoJson = ft.Static<typeof StationsGeoJson>

// ---------------------------------------------------------------------------
// sites/{id}.json
// ---------------------------------------------------------------------------

/** The full detail payload for one site: the canonical `Site`, unwrapped. */
export const SiteDetail = Site
export type SiteDetail = ft.Static<typeof SiteDetail>

// ---------------------------------------------------------------------------
// operators.json
// ---------------------------------------------------------------------------

export const OperatorSummary = objectWith(
  {
    slug: PublishedOperatorSlug,
    displayName: ft.String,
    color: ft.String,
    /** Sites attributed to this operator in this publish. */
    siteCount: ft.Number,
    /** Charge points across those sites. */
    pointCount: ft.Number,
  },
  {
    website: ft.String,
  },
)
export type OperatorSummary = ft.Static<typeof OperatorSummary>

export const OperatorsArtifact = ft.Object({
  schemaVersion: ft.Number,
  generated: IsoDateTime,
  operators: ft.Array(OperatorSummary),
})
export type OperatorsArtifact = ft.Static<typeof OperatorsArtifact>

// ---------------------------------------------------------------------------
// meta.json
// ---------------------------------------------------------------------------

/** `generated` timestamp EIPA stamped on each source file. */
export const EipaSourceTimestamps = objectWith(
  {
    operator: IsoDateTime,
    pool: IsoDateTime,
    station: IsoDateTime,
    point: IsoDateTime,
    dynamic: IsoDateTime,
  },
  {
    /**
     * Optional alone among the six: `dictionary` is published without the
     * `{data, generated}` envelope, so there is no timestamp to record.
     */
    dictionary: IsoDateTime,
  },
)
export type EipaSourceTimestamps = ft.Static<typeof EipaSourceTimestamps>

export const SourceTimestamps = objectWith(
  {
    eipa: EipaSourceTimestamps,
  },
  {
    /** When the pipeline last pulled Open Charge Map. */
    ocm: IsoDateTime,
  },
)
export type SourceTimestamps = ft.Static<typeof SourceTimestamps>

export const ArtifactCounts = ft.Object({
  operators: ft.Number,
  sites: ft.Number,
  stations: ft.Number,
  points: ft.Number,
  connectors: ft.Number,
})
export type ArtifactCounts = ft.Static<typeof ArtifactCounts>

/**
 * Connectors by which input established their voltage.
 *
 * `derivedMode` and `derivedConnector` are not the same claim and must not be
 * read as one number. A `derived-mode` figure rests on a phase count EIPA
 * itself declared, against a grid nominal fixed by IEC 60038 - about as settled
 * as a declaration. A `derived-connector` figure rests on the plug alone.
 *
 * The two declared sources are likewise separate. They are not ranked by which
 * database is better but by how the connector was found in it: `geDeclared`
 * covers both an exact EVSE-id match and a folded-address one, and the
 * `confidence` on the connector is what distinguishes those.
 */
export const VoltageSourceSplit = ft.Object({
  /** Open Charge Map published a nominal for this connector. */
  ocmDeclared: ft.Number,
  /** GoingElectric published electrics for this connector. */
  geDeclared: ft.Number,
  /** Derived from the EIPA-declared IEC 61851 charging mode. */
  derivedMode: ft.Number,
  /** Derived from the connector standard alone - the weakest of the four. */
  derivedConnector: ft.Number,
  /** Nothing established anything. */
  unknown: ft.Number,
})
export type VoltageSourceSplit = ft.Static<typeof VoltageSourceSplit>

/**
 * How many connectors got their voltage declared, derived, or not at all.
 *
 * `declared` / `derived` / `unknown` are the original three totals, kept so a
 * reader that only wants the headline still has it. The splits below exist
 * because that headline hid the thing worth knowing: derivation is strong for
 * AC, where EIPA declares the phase count, and weak for DC, where the plug is
 * usually all there is. Publishing one `derived` number made those
 * indistinguishable and made DC coverage look far better than it is.
 */
export const VoltageCoverage = ft.Object({
  /** Every declaration, whichever source made it: `ocmDeclared + geDeclared`. */
  declared: ft.Number,
  derived: ft.Number,
  unknown: ft.Number,
  /** Every connector, split by source. */
  bySource: VoltageSourceSplit,
  /** DC connectors only - where inference is weakest and the gap really sits. */
  dc: VoltageSourceSplit,
  /** AC connectors only, for contrast. */
  ac: VoltageSourceSplit,
})
export type VoltageCoverage = ft.Static<typeof VoltageCoverage>

/**
 * The site-match fields every per-operator split carries, declared once.
 *
 * Both matching stages count a site the same way - offered, matched, or not -
 * so the two blocks below share these five and differ only in what each stage
 * has to add. Spread rather than intersected: the field order is what funtypes
 * serialises with, and the published bytes are compared literally.
 */
const operatorMatchFields = {
  operatorSlug: PublishedOperatorSlug,
  /** Sites attributed to this operator that were offered to the matcher. */
  candidates: ft.Number,
  matched: ft.Number,
  unmatched: ft.Number,
  /** `matched / candidates`, 0..1, 4 decimals. */
  matchRate: ft.Number,
} as const

/**
 * One operator's slice of the match rate.
 *
 * The national number moving is a symptom; this is the diagnosis. A mapping
 * change, an operator renaming its sites, or OCM dropping a chain all show up
 * as one slug's rate collapsing while the rest hold steady.
 */
export const OperatorMatchCoverage = ft.Object(operatorMatchFields)
export type OperatorMatchCoverage = ft.Static<typeof OperatorMatchCoverage>

/**
 * One operator's slice of what GoingElectric reached.
 *
 * Wider than {@link OperatorMatchCoverage} because the site match is only half
 * of what this stage does: a network can match every one of its sites and still
 * carry almost no declarations, and the failure worth catching - the scrape
 * quietly losing a chain - shows in `connectorRate` long before it shows in
 * `matchRate`.
 *
 * `connectors` is the denominator of that rate and is published with it. The
 * national block can leave its own denominator to `counts.connectors`; a slug's
 * share of that total is not recorded anywhere else, so a reader would have no
 * way to tell a rate over eight connectors from a rate over eight hundred.
 *
 * `byEvseId` is not bounded by `matched`: the id join is per point and needs no
 * site match, so an operator with no address match at all can still carry
 * declarations here.
 */
export const OperatorGeMatchCoverage = ft.Object({
  ...operatorMatchFields,
  /** Connectors across this operator's sites - the rate's denominator. */
  connectors: ft.Number,
  connectorsDeclared: ft.Number,
  byEvseId: ft.Number,
  byAddress: ft.Number,
  /** `connectorsDeclared / connectors`, 0..1, 4 decimals. */
  connectorRate: ft.Number,
})
export type OperatorGeMatchCoverage = ft.Static<typeof OperatorGeMatchCoverage>

export const OcmMatchCoverage = ft.Object({
  /** EIPA sites considered for matching. */
  candidates: ft.Number,
  matched: ft.Number,
  unmatched: ft.Number,
  /** `matched / candidates`, 0..1. */
  matchRate: ft.Number,
  /** The same split per operator, sorted by slug so two runs compare cleanly. */
  byOperator: ft.Array(OperatorMatchCoverage),
})
export type OcmMatchCoverage = ft.Static<typeof OcmMatchCoverage>

/**
 * What the GoingElectric stage reached.
 *
 * Reported separately from `ocmMatch` rather than folded into it, because the two
 * stages answer different questions and a single "match rate" would average them
 * into something meaningless. Here the site match is only the first half of the
 * story: `connectorRate` is the number that says how much of the map carries a
 * declaration, and `byEvseId` versus `byAddress` says how firmly.
 *
 * `byOperator` splits both halves per network, because both halves fail per
 * network: a chain renames its streets and its address join stops resolving, or
 * the scrape drops it and its declarations go with it. Nationally either reads
 * as a couple of points off a rate, which is indistinguishable from the scrape
 * being a week old.
 */
export const GeMatchCoverage = ft.Object({
  /** EIPA sites considered for an address match. */
  candidates: ft.Number,
  matched: ft.Number,
  unmatched: ft.Number,
  /** `matched / candidates`, 0..1. */
  matchRate: ft.Number,
  /** Connectors that ended up with a GoingElectric declaration. */
  connectorsDeclared: ft.Number,
  /** Of those, how many came from an exact EVSE id match. */
  byEvseId: ft.Number,
  /** Of those, how many came from an address match. */
  byAddress: ft.Number,
  /** Sites published with an unconfirmed warning. See `SiteUnconfirmed`. */
  unconfirmedSites: ft.Number,
  /**
   * Sites published from this source alone, because the registry does not list
   * them. Every one of them is counted in `unconfirmedSites` too.
   *
   * The number that says whether the layer ran: it is not a rate over EIPA
   * candidates and cannot be derived from one, because these sites were never
   * candidates. A publish where it drops to zero has lost the scrape, not a join.
   */
  standaloneSites: ft.Number,
  /**
   * Connectors across those sites, and the reason it is published rather than
   * left implicit: without it nothing relates the two blocks of this artifact
   * that count this source. `candidates` is `counts.sites` less
   * `standaloneSites`, and `voltageCoverage.bySource.geDeclared` is
   * `connectorsDeclared` plus this - so a reader can check either against the
   * other instead of finding two numbers about one source that disagree.
   */
  standaloneConnectors: ft.Number,
  /** `connectorsDeclared / connectors`, 0..1. The number that matters. */
  connectorRate: ft.Number,
  /** Disagreements found while matching; the run log carries each one. */
  conflicts: ft.Number,
  /**
   * The same split per operator, sorted by slug so two runs compare cleanly.
   *
   * A partition of the national fields above, four excepted. `unconfirmedSites`
   * and `conflicts` count listings and points rather than coverage, and the run
   * log already names the site and the point code for each. `standaloneSites`
   * and `standaloneConnectors` count sites the matcher was never offered, so
   * they have no candidate population here to be a share of - which does leave
   * the standalone layer as the one part of this stage a reader cannot localise
   * to a network.
   */
  byOperator: ft.Array(OperatorGeMatchCoverage),
})
export type GeMatchCoverage = ft.Static<typeof GeMatchCoverage>

/**
 * National roll-up of `status.json`. `unavailable` is what the prose calls
 * "offline": a point the operator reports as not operational.
 */
export const AvailabilityCounts = ft.Object({
  free: ft.Number,
  occupied: ft.Number,
  unavailable: ft.Number,
  unknown: ft.Number,
})
export type AvailabilityCounts = ft.Static<typeof AvailabilityCounts>

export const MetaArtifact = ft.Object({
  schemaVersion: ft.Number,
  /** Version of the pipeline that produced this set, e.g. `0.1.0`. */
  pipelineVersion: ft.String,
  /** When this artifact set was published. */
  generated: IsoDateTime,
  /** When the pipeline run started and finished. */
  startedAt: IsoDateTime,
  finishedAt: IsoDateTime,
  sources: SourceTimestamps,
  counts: ArtifactCounts,
  voltageCoverage: VoltageCoverage,
  ocmMatch: OcmMatchCoverage,
  geMatch: GeMatchCoverage,
  availability: AvailabilityCounts,
  /**
   * True when this set was published without one of the sources that *declare*
   * electrics - Open Charge Map, GoingElectric, or both - so more of the map is
   * inferred than a healthy run would leave inferred. The frontend says so rather
   * than presenting derived numbers as though an operator had published them.
   *
   * Which source was missing is not in this flag; the run log names it, and the
   * corresponding `voltageCoverage.bySource` field being zero shows it.
   */
  degraded: ft.Boolean,
})
export type MetaArtifact = ft.Static<typeof MetaArtifact>

// ---------------------------------------------------------------------------
// status.json
// ---------------------------------------------------------------------------

export const PointAvailability = ft.Union(
  ft.Literal('free'),
  ft.Literal('occupied'),
  /** Operator reports the point as not operational. */
  ft.Literal('unavailable'),
  /** No dynamic row for this point at all. */
  ft.Literal('unknown'),
)
export type PointAvailability = ft.Static<typeof PointAvailability>

export const PointStatus = objectWith(
  { state: PointAvailability },
  { /** When the operator last updated this status. */ ts: IsoDateTime },
)
export type PointStatus = ft.Static<typeof PointStatus>

export const SiteStatus = ft.Object({
  free: ft.Number,
  occupied: ft.Number,
  unavailable: ft.Number,
  unknown: ft.Number,
  /** Charge point id -> status. */
  points: ft.Record(ft.String, PointStatus),
})
export type SiteStatus = ft.Static<typeof SiteStatus>

export const StatusArtifact = ft.Object({
  schemaVersion: ft.Number,
  generated: IsoDateTime,
  /** Site id -> aggregated status. */
  sites: ft.Record(ft.String, SiteStatus),
})
export type StatusArtifact = ft.Static<typeof StatusArtifact>

// ---------------------------------------------------------------------------
// prices.json
// ---------------------------------------------------------------------------

/**
 * Ad-hoc tariff for a site. All three fields are optional: EIPA lets an
 * operator publish a per-kWh price, a per-minute price, a free-text tariff, or
 * any combination.
 */
export const SitePrice = ft.Partial({
  pricePlnPerKwh: ft.Number,
  pricePlnPerMin: ft.Number,
  /** Free-text tariff, verbatim from the operator. */
  note: ft.String,
})
export type SitePrice = ft.Static<typeof SitePrice>

export const PricesArtifact = ft.Object({
  schemaVersion: ft.Number,
  generated: IsoDateTime,
  /** Site id -> tariff. */
  sites: ft.Record(ft.String, SitePrice),
})
export type PricesArtifact = ft.Static<typeof PricesArtifact>

// ---------------------------------------------------------------------------
// The set
// ---------------------------------------------------------------------------

/** Every artifact the frontend can fetch, minus the per-site detail payloads. */
export interface ArtifactSet {
  readonly stations: StationsGeoJson
  readonly operators: OperatorsArtifact
  readonly meta: MetaArtifact
  readonly status: StatusArtifact
  readonly prices: PricesArtifact
}
